<template>
  <div
    :ref="composedDialogRef"
    :class="[
      ns.b(),
      ns.is('fullscreen', fullscreen),
      ns.is('draggable', draggable),
      { [ns.m('center')]: center },
      containerClass,
    ]"
    :style="style"
    tabindex="-1"
    @click.stop
  >
    <header ref="headerRef" :class="[ns.e('header'), headerClass]">
      <slot name="header">
        <span role="heading" :class="ns.e('title')">
          {{ title }}
        </span>
      </slot>
      <b-button
        v-if="showClose"
        small
        ghost
        :aria-label="t('b.dialog.close')"
        :icon="closeIcon || Close"
        @click="$emit('close')"
      />
    </header>
    <div :id="bodyId" :class="[ns.e('body'), bodyClass]">
      <b-scrollbar
        v-if="scrollable"
        :height="scrollableHeight"
        :max-height="scrollableMaxHeight"
        @scroll="($event) => emit('scroll', $event)"
      >
        <slot />
      </b-scrollbar>
      <slot v-else />
    </div>
    <footer v-if="$slots.footer" :class="[ns.e('footer'), footerClass]">
      <slot name="footer" />
    </footer>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject } from 'vue'
import { BButton } from '@bigin/components/button'
import { BScrollbar } from '@bigin/components/scrollbar'
import { FOCUS_TRAP_INJECTION_KEY } from '@bigin/components/focus-trap'
import { useDraggable, useLocale } from '@bigin/hooks'
import { CloseComponents, composeRefs } from '@bigin/utils'
import { dialogInjectionKey } from '@bigin/tokens'
import { dialogContentEmits, dialogContentProps } from './dialog-content'

const { t } = useLocale()
const { Close } = CloseComponents

defineOptions({ name: 'BDialogContent' })
const props = defineProps(dialogContentProps)
const emit = defineEmits(dialogContentEmits)

const { dialogRef, headerRef, bodyId, ns, style } = inject(dialogInjectionKey)!
const { focusTrapRef } = inject(FOCUS_TRAP_INJECTION_KEY)!

const composedDialogRef = composeRefs(focusTrapRef, dialogRef)

const draggable = computed(() => props.draggable)
useDraggable(dialogRef, headerRef, draggable)
</script>
